// PerformanceMonitor.js - Monitoring de performance frontend pour load testing
class PerformanceMonitor {
  constructor() {
    this.metrics = {
      loginTimes: [],
      navigationTimes: [],
      memoryUsage: [],
      apiResponseTimes: [],
      errorCounts: {
        network: 0,
        auth: 0,
        validation: 0,
        server: 0
      }
    };

    this.isMonitoring = false;
    this.observers = [];
  }

  startMonitoring() {
    if (this.isMonitoring) {
      console.log('🔍 Performance monitoring already active');
      return;
    }

    this.isMonitoring = true;
    console.log('🚀 Starting frontend performance monitoring...');

    this.setupPerformanceObserver();
    this.setupMemoryMonitoring();
    this.setupAPIMonitoring();
    this.setupErrorTracking();
  }

  stopMonitoring() {
    this.isMonitoring = false;
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    console.log('🛑 Performance monitoring stopped');
  }

  setupPerformanceObserver() {
    if (!window.PerformanceObserver) {
      console.warn('PerformanceObserver not supported');
      return;
    }

    // Navigation timing observer
    const navObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        const timing = {
          name: entry.name,
          duration: entry.duration,
          type: entry.entryType,
          timestamp: Date.now()
        };

        this.metrics.navigationTimes.push(timing);

        if (entry.duration > 1000) {
          console.warn(`⚠️ Slow navigation: ${entry.name} took ${entry.duration.toFixed(2)}ms`);
        }

        this.logMetric('navigation', timing);
      });
    });

    navObserver.observe({ entryTypes: ['navigation', 'measure'] });
    this.observers.push(navObserver);

    // Resource timing observer
    const resourceObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.name.includes('/api/')) {
          const apiTiming = {
            url: entry.name,
            duration: entry.duration,
            responseStart: entry.responseStart,
            responseEnd: entry.responseEnd,
            timestamp: Date.now()
          };

          this.metrics.apiResponseTimes.push(apiTiming);
          this.logMetric('api', apiTiming);
        }
      });
    });

    resourceObserver.observe({ entryTypes: ['resource'] });
    this.observers.push(resourceObserver);
  }

  setupMemoryMonitoring() {
    if (!performance.memory) {
      console.warn('Performance.memory not available');
      return;
    }

    const monitorMemory = () => {
      if (!this.isMonitoring) return;

      const memInfo = {
        used: Math.round(performance.memory.usedJSHeapSize / 1048576), // MB
        total: Math.round(performance.memory.totalJSHeapSize / 1048576), // MB
        limit: Math.round(performance.memory.jsHeapSizeLimit / 1048576), // MB
        timestamp: Date.now()
      };

      this.metrics.memoryUsage.push(memInfo);

      // Alert if memory usage is high
      if (memInfo.used > 100) { // > 100MB
        console.warn(`⚠️ High memory usage: ${memInfo.used}MB`);
      }

      this.logMetric('memory', memInfo);

      setTimeout(monitorMemory, 5000); // Check every 5 seconds
    };

    monitorMemory();
  }

  setupAPIMonitoring() {
    // Intercept fetch requests to monitor API calls
    const originalFetch = window.fetch;

    window.fetch = async (url, options = {}) => {
      const startTime = performance.now();

      try {
        const response = await originalFetch(url, options);
        const endTime = performance.now();
        const duration = endTime - startTime;

        // Log API performance
        if (url.includes('/api/')) {
          const apiMetric = {
            url: url.toString(),
            method: options.method || 'GET',
            status: response.status,
            duration: duration,
            success: response.ok,
            timestamp: Date.now()
          };

          this.metrics.apiResponseTimes.push(apiMetric);

          if (duration > 2000) { // > 2 seconds
            console.warn(`⚠️ Slow API call: ${url} took ${duration.toFixed(2)}ms`);
          }

          this.logMetric('api-detailed', apiMetric);
        }

        return response;
      } catch (error) {
        const endTime = performance.now();
        const duration = endTime - startTime;

        this.trackError('network', {
          url: url.toString(),
          error: error.message,
          duration
        });

        throw error;
      }
    };
  }

  setupErrorTracking() {
    // Track unhandled errors
    window.addEventListener('error', (event) => {
      this.trackError('general', {
        message: event.message,
        filename: event.filename,
        line: event.lineno,
        column: event.colno
      });
    });

    // Track unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError('promise', {
        reason: event.reason
      });
    });
  }

  // Performance markers for specific operations
  markLoginStart() {
    try {
      // Nettoyer les marks précédents pour éviter les conflits
      performance.clearMarks('login-start');
      performance.clearMarks('login-end');
      performance.clearMeasures('login-duration');

      performance.mark('login-start');
      console.log('📊 Login performance tracking started');
    } catch (error) {
      console.warn('⚠️ Failed to start login performance tracking:', error.message);
    }
  }

  markLoginEnd() {
    try {
      performance.mark('login-end');

      // Vérifier que le mark de début existe
      const marks = performance.getEntriesByName('login-start');
      if (marks.length === 0) {
        console.warn('⚠️ Performance: login-start mark not found, skipping measurement');
        // Enregistrer sans durée
        this.logMetric('login', {
          event: 'login_success_no_timing',
          timestamp: Date.now()
        });
        return;
      }

      performance.measure('login-duration', 'login-start', 'login-end');

      const measure = performance.getEntriesByName('login-duration')[0];
      if (measure) {
        this.metrics.loginTimes.push({
          duration: measure.duration,
          timestamp: Date.now()
        });

        console.log(`📊 Login completed in ${measure.duration.toFixed(2)}ms`);
        this.logMetric('login', { duration: measure.duration });
      }
    } catch (error) {
      console.warn('⚠️ Performance measurement failed:', error.message);
      // Fallback: enregistrer sans durée
      this.logMetric('login', {
        event: 'login_success_no_timing',
        timestamp: Date.now()
      });
    }
  }

  // Ajout des méthodes trackLogin, trackLoginFailure, trackLogout
  trackLogin(userId) {
    this.markLoginEnd(); // Utilise markLoginEnd pour enregistrer la durée
    console.log(`📈 User logged in: ${userId}`);
    this.logMetric('auth', { event: 'login_success', userId, timestamp: Date.now() });
  }

  trackLoginFailure(email, errorMessage) {
    // Pas besoin de marquer le début/fin ici car l'échec peut survenir avant la fin normale
    console.error(`📉 Login failed for ${email}: ${errorMessage}`);
    this.trackError('auth', {
      event: 'login_failure',
      email,
      error: errorMessage,
      timestamp: Date.now()
    });
  }

  trackLogout(userId) {
    console.log(`📉 User logged out: ${userId || 'unknown'}`);
    this.logMetric('auth', { event: 'logout', userId, timestamp: Date.now() });
  }

  markLoadTestStart() {
    performance.mark('load-test-start');
    this.startTime = Date.now();
    console.log('🔥 Load test performance tracking started');
  }
  markLoadTestEnd() {
    performance.mark('load-test-end');
    performance.measure('load-test-duration', 'load-test-start', 'load-test-end');

    const summary = this.generateSummary();

    console.log('🔥 Load test completed - Performance Summary:', summary);
    return summary;
  }

  trackError(type, details) {
    this.metrics.errorCounts[type] = (this.metrics.errorCounts[type] || 0) + 1;

    console.error(`❌ Error tracked [${type}]:`, details);
    this.logMetric('error', { type, details, timestamp: Date.now() });
  }

  logMetric(type, data) {
    if (window.digitalCloud360Debug) {
      console.log(`📈 [${type.toUpperCase()}]`, data);
    }
  }

  generateSummary() {
    const summary = {
      monitoring_duration: this.startTime ? Date.now() - this.startTime : 0,
      login_performance: {
        count: this.metrics.loginTimes.length,
        avg_duration: this.calculateAverage(this.metrics.loginTimes, 'duration'),
        max_duration: this.calculateMax(this.metrics.loginTimes, 'duration'),
        min_duration: this.calculateMin(this.metrics.loginTimes, 'duration')
      },
      api_performance: {
        count: this.metrics.apiResponseTimes.length,
        avg_duration: this.calculateAverage(this.metrics.apiResponseTimes, 'duration'),
        success_rate: this.calculateSuccessRate(this.metrics.apiResponseTimes)
      },
      memory_usage: {
        peak: this.calculateMax(this.metrics.memoryUsage, 'used'),
        average: this.calculateAverage(this.metrics.memoryUsage, 'used'),
        samples: this.metrics.memoryUsage.length
      },
      errors: { ...this.metrics.errorCounts },
      timestamp: new Date().toISOString()
    };

    return summary;
  }

  calculateAverage(array, property) {
    if (array.length === 0) return 0;
    const sum = array.reduce((acc, item) => acc + (item[property] || 0), 0);
    return Math.round(sum / array.length * 100) / 100;
  }

  calculateMax(array, property) {
    if (array.length === 0) return 0;
    return Math.max(...array.map(item => item[property] || 0));
  }

  calculateMin(array, property) {
    if (array.length === 0) return 0;
    return Math.min(...array.map(item => item[property] || 0));
  }

  calculateSuccessRate(apiCalls) {
    if (apiCalls.length === 0) return 0;
    const successful = apiCalls.filter(call => call.success !== false).length;
    return Math.round(successful / apiCalls.length * 100);
  }

  // Export metrics for external monitoring
  exportMetrics() {
    return {
      summary: this.generateSummary(),
      raw_data: this.metrics,
      export_time: new Date().toISOString()
    };
  }

  // Clear metrics (useful for multiple test runs)
  clearMetrics() {
    this.metrics = {
      loginTimes: [],
      navigationTimes: [],
      memoryUsage: [],
      apiResponseTimes: [],
      errorCounts: {
        network: 0,
        auth: 0,
        validation: 0,
        server: 0
      }
    };
    console.log('🧹 Performance metrics cleared');
  }
}

// Global instance
const performanceMonitor = new PerformanceMonitor();

// Enable debug mode for detailed logging
window.digitalCloud360Debug = process.env.NODE_ENV === 'development';

export default performanceMonitor;
